import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Skeleton,
  Pagination,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import Page from 'src/components/Page';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TermsConditionModal from 'src/sections/TermsConditionModal';
import TermsConditionViewModal from 'src/sections/dashboard-sections/TermsConditionViewModal';
import {
  getTermsCondition,
  addTermsCondition,
  deleteTermsCondition,
  makeActiveTerms
} from 'src/ApiServices';

export default function TermsCondition() {
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bestModalOpen, setBestModalOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedTermForBest, setSelectedTermForBest] = useState(null);
  const [currentBestStatus, setCurrentBestStatus] = useState(false);
  const [bestLoading, setBestLoading] = useState(false);
  const [termsData, setTermsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  const fetchTermsData = async (currentPage = 1) => {
    try {
      setLoading(true);
      const response = await getTermsCondition({ page: currentPage });
      const transformedData = transformApiData(response.data || []);
      setTermsData(transformedData);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching terms:', error);
      setTermsData([]);
      toast.error('Failed to load terms & conditions');
    } finally {
      setLoading(false);
    }
  };

  const extractTextFromHTML = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  };

  const extractTitleFromHTML = (html) => {
    const text = extractTextFromHTML(html);
    if (text.length <= 50) return text;
    return text.substring(0, 50) + '...';
  };

  const transformApiData = (apiData) => {
    return apiData
      .map((term) => {
        let contents = [];

        if (term.value) {
          try {
            if (term.value.startsWith('[')) {
              const parsedValue = JSON.parse(term.value);
              if (Array.isArray(parsedValue)) {
                contents = parsedValue.map((item, index) => ({
                  id: index,
                  paragraph: item.paragraph || '',
                  type: item.type || 'paragraph'
                }));
              }
            } else {
              contents = [
                {
                  id: 0,
                  paragraph: term.value,
                  type: 'paragraph'
                }
              ];
            }
          } catch (error) {
            console.error('Error parsing term value:', error);
            contents = [
              {
                id: 0,
                paragraph: term.value,
                type: 'paragraph'
              }
            ];
          }
        }

        const title = term.company_policy || extractTitleFromHTML(contents[0]?.paragraph || '');

        return {
          id: term.id,
          main_id: term.id,
          title: title,
          status: term.status === 'active' ? 'Active' : 'Inactive',
          isActive: term.status === 'active',
          is_best: parseInt(term.is_best) || 0,
          created_at: term.created_at,
          updated_at: term.updated_at,
          contents: contents,
          company_policy: term.company_policy,
          value: term.value,
          type: term.type
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
  };

  useEffect(() => {
    fetchTermsData(page);
  }, [page]);

  const handleAddClick = () => {
    setSelectedTerm(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleEditClick = (term) => {
    setSelectedTerm(term);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleViewClick = (term) => {
    setSelectedTerm(term);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (term) => {
    setSelectedTerm(term);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTerm) return;
    try {
      setDeleteLoading(true);
      await deleteTermsCondition(selectedTerm.id);
      toast.success('Terms & Conditions deleted successfully!');
      fetchTermsData(page);
      setDeleteModalOpen(false);
      setSelectedTerm(null);
    } catch (error) {
      console.error('Failed to delete term:', error);
      toast.error(error.response?.data?.message || 'Failed to delete term!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedTerm(null);
  };

  // Best Property Toggle Logic
  const handleBestToggle = (term) => {
    const termId = term.id;
    const currentBestStatus = term.is_best === 1;

    if (currentBestStatus) {
      performBestToggle(term, currentBestStatus);
      return;
    }

    const existingBestTerm = termsData.find((t) => t.is_best === 1 && t.id !== termId);

    if (existingBestTerm) {
      setSelectedTermForBest(term);
      setCurrentBestStatus(currentBestStatus);
      setBestModalOpen(true);
    } else {
      performBestToggle(term, currentBestStatus);
    }
  };

  const performBestToggle = async (term, currentBestStatus) => {
    const termId = term.id;
    const newBestStatus = currentBestStatus ? 0 : 1;
    const previousTerms = [...termsData];

    setTermsData((prev) =>
      prev.map((t) => ({
        ...t,
        is_best: t.id === termId ? newBestStatus : newBestStatus ? 0 : t.is_best
      }))
    );

    try {
      await makeActiveTerms(termId, newBestStatus);
      toast.success(`Terms ${newBestStatus ? 'marked as' : 'removed from'} Best Terms!`);
      await fetchTermsData(page);
    } catch (error) {
      setTermsData(previousTerms);
      toast.error('Failed to update Best Terms status');
      console.error('Error saving best terms:', error);
    }
  };

  const handleBestConfirm = async () => {
    if (!selectedTermForBest) return;

    try {
      setBestLoading(true);
      await performBestToggle(selectedTermForBest, currentBestStatus);
      setBestModalOpen(false);
      setSelectedTermForBest(null);
    } catch (error) {
      console.error('Failed to update best terms:', error);
    } finally {
      setBestLoading(false);
    }
  };

  const handleBestCancel = () => {
    setBestModalOpen(false);
    setSelectedTermForBest(null);
    setCurrentBestStatus(false);
  };

  const handleModalSubmit = async (formData) => {
    try {
      const termDetailsArray = formData.contents.map((contentItem) => {
        return {
          paragraph: contentItem.content,
          type: contentItem.type.toLowerCase().replace(' point', '')
        };
      });

      const payloadObject = {
        heading: extractTitleFromHTML(formData.contents[0]?.content || ''),
        value: JSON.stringify(termDetailsArray)
      };

      if (isEditing && selectedTerm) {
        payloadObject.id = selectedTerm.id;
      }

      await addTermsCondition(payloadObject);
      toast.success(isEditing ? 'Terms updated successfully!' : 'Terms added successfully!');

      fetchTermsData(page);
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving term:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors ||
        `Failed to ${isEditing ? 'update' : 'add'} term`;
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const tableHeaders = ['Title', 'Content', 'Status', 'Mark as Active', 'Actions'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {tableHeaders.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" width={cellIndex === 1 ? 200 : 120} height={25} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = () => (
    <TableRow>
      <TableCell
        colSpan={tableHeaders.length}
        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            No Terms & Conditions Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            No terms & conditions available. Add new terms to get started.
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Page title={`Terms & Conditions | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Terms & Conditions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              minWidth: 140,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Add Terms
          </Button>
        </Box>

        <Card>
          <TableContainer
            sx={{
              overflowX: 'auto',
              width: '100%'
            }}
          >
            <Table
              sx={{
                minWidth: 1600,
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paper,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '14px',
                  borderBottom: `2px solid ${palette.divider}`,
                  py: 2
                },
                '& .MuiTableCell-body': {
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  borderBottom: `1px solid ${palette.divider}`,
                  py: 2
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? renderSkeletonRows()
                  : termsData.length > 0
                  ? termsData.map((term) => {
                      const title =
                        term.company_policy ||
                        extractTitleFromHTML(term.contents[0]?.paragraph || '');

                      const allContent = term.contents
                        .map((item) => extractTextFromHTML(item.paragraph))
                        .join(' ');

                      const contentPreview =
                        allContent.length > 100 ? allContent.substring(0, 100) + '...' : allContent;

                      return (
                        <TableRow
                          key={term.id}
                          sx={{ '&:hover': { backgroundColor: palette.action.hover } }}
                        >
                          <TableCell sx={{ px: 2 }}>{title}</TableCell>
                          <TableCell sx={{ px: 2 }}>{contentPreview}</TableCell>
                          <TableCell sx={{ px: 2 }}>{term.status}</TableCell>
                          <TableCell sx={{ px: 2, textAlign: 'center' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={term.is_best === 1}
                                  onChange={() => handleBestToggle(term)}
                                  color="primary"
                                />
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ px: 2, textAlign: 'center' }}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(term)}
                                sx={{ color: palette.info.main, mr: 1 }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(term)}
                                sx={{ color: palette.primary.main, mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(term)}
                                sx={{ color: palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : renderNoRecordsFound()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {!loading && termsData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <TermsConditionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
          term={selectedTerm}
          isEditing={isEditing}
        />

        <TermsConditionViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          term={selectedTerm}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          PaperProps={{ sx: { borderRadius: 2, padding: 1, m: 2 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon sx={{ fontSize: 60, color: palette.warning.main, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete this terms & conditions?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3, flexWrap: 'wrap' }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': { backgroundColor: palette.error.dark }
              }}
              disabled={deleteLoading}
              startIcon={<DeleteIcon />}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Best Property Confirmation Modal */}
        <Dialog
          open={bestModalOpen}
          onClose={handleBestCancel}
          PaperProps={{ sx: { borderRadius: 2, padding: 1, m: 2 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon sx={{ fontSize: 60, color: palette.warning.main, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Change Best Terms
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to mark <strong>{selectedTermForBest?.title}</strong> as Active
              Terms?
              <br />
              The current Active terms will be automatically replaced.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3, flexWrap: 'wrap' }}>
            <Button
              onClick={handleBestCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={bestLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBestConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.primary.main,
                color: palette.primary.contrastText,
                minWidth: 100,
                '&:hover': { backgroundColor: palette.primary.dark }
              }}
              disabled={bestLoading}
            >
              {bestLoading ? 'Updating...' : 'Mark as Active'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
